import { useState, useEffect } from "react";
import moment from "moment";

const dayMapping = {
  월: "Monday",
  화: "Tuesday",
  수: "Wednesday",
  목: "Thursday",
  금: "Friday",
  토: "Saturday",
  일: "Sunday",
  매일: "Everyday",
};

function parseBusinessHours(businessHours) {
  if (!businessHours) return {};
  const lines = businessHours
    ?.split("\n")
    ?.map((line) => line?.trim())
    ?.filter((line) => line);
  const hours = {};
  let alwaysOpen = false;

  const timeRegex = /(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/;

  for (let line of lines) {
    if (line.includes("매일 00:00 - 24:00")) {
      alwaysOpen = true;
      continue;
    }

    let dayMatch = line.match(/([월화수목금토일매일]+)/);
    let timeMatch = line.match(timeRegex);

    if (dayMatch && timeMatch) {
      let days =
        dayMatch[1] === "매일"
          ? [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ]
          : [dayMapping[dayMatch[1]]];

      let openTime = `${timeMatch[1]}:${timeMatch[2]}`;
      let closeTime = `${timeMatch[3]}:${timeMatch[4]}`;

      days.forEach((day) => {
        if (!hours[day]) hours[day] = [];
        hours[day].push({ open: openTime, close: closeTime });
      });
    }
  }

  return { hours, alwaysOpen };
}

function isStoreOpen(data) {
  if (data?.alwaysOpen) return true;

  const currentDay = moment().format("dddd");
  const currentDateString = moment().format("YYYY-MM-DD");
  const currentTime = moment().format("HH:mm");

  if (
    data?.hours?.["SpecialDates"] &&
    data?.hours?.["SpecialDates"][currentDateString]
  ) {
    const { open, close } = data.hours["SpecialDates"][currentDateString];
    return currentTime >= open && currentTime <= close;
  }

  if (!data?.hours?.[currentDay]) return false;

  return data?.hours?.[currentDay]?.some(({ open, close }) => {
    return currentTime >= open && currentTime <= close;
  });
}

/**
 * React Hook to check if the store is open based on business hours.
 * @param {string} businessHours - Korean business hours string.
 * @returns {boolean} - Store open status.
 */
export function useBusinessHours(businessHours) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const parsedData = parseBusinessHours(businessHours);

    const checkStatus = () => {
      setIsOpen(isStoreOpen(parsedData));
    };

    checkStatus();
    const interval = setInterval(checkStatus, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [businessHours]);

  return isOpen;
}
